import { ProFormField, QueryFilter, QueryFilterProps } from '@ant-design/pro-form';
import { ProBuglyDatePicker, ProDatePickerProps } from '@bugly/common/src/form/pro-date-picker';

import { useLocalStorageState } from 'ahooks';
import { Button, SelectProps } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import classNames from 'classnames';
import { difference } from 'lodash-es';
import { Moment } from 'moment/moment';
import React, { CSSProperties, forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ReportGuideType } from '@/config/app/auth';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick, ReportScene } from '@/infrastructure/reporter/datong/report';
import { TimeRange } from '@/infrastructure/utils/timestamp';
import { FormCompare } from '@/presentation/components/form-compare';
import { CustomFields } from '@/presentation/components/form/components/custom-fields';
import { DateTimeRange } from '@/presentation/components/form/components/date-time-range';
import { DetailSelectGroupForm } from '@/presentation/components/form/components/detail-select-group';
import { InputForm } from '@/presentation/components/form/components/input';
import { RadioGroupForm } from '@/presentation/components/form/components/radio';
import { RulesFields } from '@/presentation/components/form/components/rules';
import { SelectForm } from '@/presentation/components/form/components/select';
import { SelectGroupForm } from '@/presentation/components/form/components/select-group';
import { SelectInputForm } from '@/presentation/components/form/components/select-input';
import {
  NumberType,
  SelectNumberForm,
} from '@/presentation/components/form/components/select-number';
import { SwitchForm } from '@/presentation/components/form/components/switch';
import { TimeRangeForm } from '@/presentation/components/form/components/time-range';
import { TimeRangeCascadeForm } from '@/presentation/components/form/components/time-range-cascade';
import { VersionSelectForm } from '@/presentation/components/form/components/version-select';
import { VersionSelectGroupForm } from '@/presentation/components/form/components/version-select-group';
import stylesForm from '@/presentation/components/form/index.module.scss';
import { DEFAULT_SLICE_ITEM_NUMBER } from '@/presentation/components/form/proto';
import { FilterPanel } from '@/presentation/components/form/setting/panel';
import { SaveSearch } from '@/presentation/components/form/setting/save-search';
import { CharacterInputForm } from '@/presentation/components/form/standards/character-input';
import { CharacterRangePickerProps } from '@/presentation/components/form/standards/range';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import { AreaForm } from '@/presentation/components/tds-form/tds-area';
import { SearchValueStorageType } from '@/presentation/config/type';
import { useStores } from '@/presentation/store';
import {
  ExOption,
  FieldKey,
  FieldScope,
  LaunchType,
  Option, Zone,
} from '@/proto/field';
import { MetricSubType, MetricType, TimeType } from '@/proto/metric';
import { IUserKey } from '@/proto/user';
import { IProductType, ProductType } from '@/service/product-permissions';
import { toHumpString } from '@raft/utils/src/case/camel-case';
import { AutoCompleteForm } from './components/auto-complete';
import styles from './index.module.scss';
import { filterFormValues, getCachedFilters, getPgIdByIssueType, setCachedFilters } from './utils';

export enum FormType {
  TimeRange,
  Input,
  Select,
  SelectGroup,
  SelectInputGroup,
  SelectNumberGroup,
  Switch,
  RadioGroup,
  CustomFields,
  VersionSelect,
  CustomKeys,
  BuglyCustomKeys,
  AutoComplete,
  TimeRangeCascade,
  VersionSelectGroup,
  DetailSelectGroup,
  DateTimeRange,
  ProDatePicker,
  Rules,
  CharacterInput,
  TdsArea
}

export type BaseFormType = {
  labelTools?: React.ReactNode[];
  name: string;
  label: string | React.ReactNode;
  keys?: IUserKey[];
  labelString?: string;
  deleteable?: boolean;
  // 废弃
  initialDeleted?: boolean;
  disabled?: boolean;
  initialValue?: any;
  convertValue?: (value: any) => any;
  transform?: (value: any, namePath: string, allValues: any) => string | Record<string, any>;
  compareDisabled?: boolean;
  placeholder?: string;
  filterOption?: (inputValue: string, option: any) => boolean;
  longSearch?: boolean;
  dropdownMatchSelectWidth?: boolean | number;
  enableExcludeMode?: boolean;
  enableAutoSearch?: boolean;// 是否支持选中改变时自动发起查询
  formType?: IProductType[];// 表单类型，免费产品只支持免费的表单,其他隐藏
  group?: FormColumnGroup;
};

export type CharacterInputFormType = BaseFormType & {
  type: FormType.CharacterInput;
  fieldKey?: FieldKey;
  fieldScope?: FieldScope;
  options?: Option[] | ExOption[];
  modeSelectProps?: SelectProps;
  valueSelectProps?: SelectProps;
  mode: 'multiple' | 'tags' | 'single';
  modeOptions?: Option[] | ExOption[];
  rangeProps?: CharacterRangePickerProps;
  isSplit?: boolean;
};

export type AutoCompleteFormType = BaseFormType & {
  type: FormType.AutoComplete;
  fieldKey?: FieldKey;
  fieldScope?: FieldScope;
  options?: Option[] | ExOption[];
};

export type CustomFieldsFormType = BaseFormType & {
  type: FormType.CustomFields;
};

export type CustomKeysFormType = BaseFormType & {
  type: FormType.CustomKeys;
};


export type BuglyCustomKeysFormType = BaseFormType & {
  type: FormType.BuglyCustomKeys;
};

export type VersionSelectFormType = BaseFormType & {
  type: FormType.VersionSelect;
  versionName: string;
  fieldKey: FieldKey;
  fieldScope: FieldScope;
};

export type TimeRangeFormType = BaseFormType & {
  type: FormType.TimeRange;
  initRange?: string[];
  scope: 'analytics' | 'issues';
  ranges?: Record<string, TimeRange>;
  showTime?: boolean;
  // 限制当前最多可选择天数
  limitDays?: number;
  dateChange?: (value: TimeRange) => void;
  timeTypeOptions?: Option[];
  hiddenType?: boolean;
};

interface TimeQuickProps extends SelectProps<string | Option, Option> {
  timeQuickOnChange?: (value: (string | Option),
    option: Option | Option[],
    updateTimeQuickProps: React.Dispatch<React.SetStateAction<SelectProps<string | Option, Option>>>,
    updateRangePickerProps: React.Dispatch<React.SetStateAction<RangePickerProps>>
  ) => void;
}
type PickerProps = RangePickerProps & {
  rangePickerOnChange?: (values: [Moment | null, Moment | null] | null) => void;
  ref?: React.RefObject<any>;
};
export type DateTimeRangeFormType = BaseFormType & {
  type: FormType.DateTimeRange;
  timeQuickProps?: TimeQuickProps;
  rangePickerProps?: PickerProps;
};

export type TimeRangeCascadeFormType = BaseFormType & {
  type: FormType.TimeRangeCascade;
  initTimeWindow?: TimeType;
  initRange?: string[];
  scope: 'analytics' | 'issues';
  ranges?: Record<string, TimeRange>;
  showTime?: boolean;
  // 限制当前最多可选择天数
  limitDays?: number;
  metricType?: MetricType;
  subType?: LaunchType | MetricType | MetricSubType;
  isCompareOpen?: boolean;
  hiddenTimeWindow?: boolean;
  hiddenTimeQuick?: boolean;
  hiddenTimeRange?: boolean;
  hiddenTimeType?: boolean;
  isNotRange?: boolean;
  timeQuickOptions?: Option[];
  timeTypeOptions?: Option[];
  isIssuesTimeWindow?: boolean;
  getTimeQuickOptions?: (timeWindow: TimeType) => Record<string, any>;
  customTimeWindowOptions?: { label: string, value: TimeType }[];
  // 默认时间返回的timeQuick初始值
  defaultTimeQuick?: string;
};

export type ProDatePickerFormType = BaseFormType & {
  type: FormType.ProDatePicker;
  fieldProps?: ProDatePickerProps;
};

export type InputFormType = BaseFormType & {
  type: FormType.Input;
  rules?: Record<string, any>[];
  defaultOptions?: ExOption[];
};

export type SelectInputFormType = BaseFormType & {
  type: FormType.SelectInputGroup;
  leftName: string;
  rightName: string;
  leftInitialValue?: any;
  rightInitialValue?: string;
  fieldKey: FieldKey;
  fieldScope: FieldScope;
};

export type SelectNumberFormType = BaseFormType & {
  type: FormType.SelectNumberGroup;
  name: string;
  options: Option[];
  processValue?: (value: NumberType, allValues?: any) => any;
  convertValue?: (value: any, field: string | number | (string | number)[]) => string | Record<string, any>;
  transform?: (value: any, namePath: string, allValues: any) => string | Record<string, any>;
  min?: number;
  max?: number;
  dropDownMode?: boolean;
};

type BaseSelectFormType = BaseFormType & {
  type: FormType.Select;
  mode: 'multiple' | 'tags' | 'single';
  initialValue?: any;
  placeholder?: string;
  dropdownMatchSelectWidth?: boolean | number;
  options?: Option[] | ExOption[];
  labelTools?: React.ReactNode[];
};

export type SelectFormTypeField = BaseSelectFormType & {
  fieldKey: FieldKey;
  fieldScope: FieldScope;
};

export type SelectFormTypeOption = BaseSelectFormType & {
  options: Option[] | ExOption[];
};

type BaseSelectGroupFormType = BaseFormType & {
  type: FormType.SelectGroup;
  leftName: string;
  rightName: string;
  mode: 'multiple' | 'tags' | 'single';
  leftInitialValue?: any;
  rightInitialValue?: any;
};

export type VersionSelectGroupFormType = BaseFormType & {
  type: FormType.VersionSelectGroup;
  leftName: string;
  rightName: string;
  mode: 'multiple' | 'tags' | 'single';
  leftFieldKey: FieldKey;
  rightFieldKey: FieldKey;
  fieldScope?: FieldScope;
  scope?: 'analytics' | 'issues';
  leftOptions?: Option[] | ExOption[];
  rightOptions?: Option[] | ExOption[];
  leftInitialValue?: any;
  rightInitialValue?: any;
  subType?: LaunchType | MetricType | MetricSubType;
  isCompareOpen?: boolean;
  hasMaxWidth?: boolean;
  isSplit?: true,
};

export type DetailSelectGroupFormType = BaseFormType & {
  type: FormType.DetailSelectGroup;
  leftName: string;
  centerName: string;
  rightName: string;
  leftFieldKey: FieldKey;
  centerFieldKey: FieldKey;
  fieldScope?: FieldScope;
  scope?: 'analytics' | 'issues';
  leftOptions?: Option[] | ExOption[];
  rightOptions?: Option[] | ExOption[];
  centerOptions?: Option[] | ExOption[];
  leftInitialValue?: any;
  rightInitialValue?: any;
  centerInitialValue?: any;
  subType?: LaunchType | MetricType | MetricSubType;
  isCompareOpen?: boolean;
  hasMaxWidth?: boolean;
  showLeftSelect?: boolean;
  rightClassName?: string;
};

export type SelectGroupFormTypeField = BaseSelectGroupFormType & {
  leftFieldKey: FieldKey;
  rightFieldKey: FieldKey;
  fieldScope: FieldScope;
  rightProps?: SelectProps;
};

export type SelectGroupFormTypeOption = BaseSelectGroupFormType & {
  leftOptions: Option[] | ExOption[];
  rightOptions: Option[] | ExOption[];
  rightProps?: SelectProps;
};

export type SwitchFormType = BaseFormType & {
  type: FormType.Switch;
};

export type RadioGroupFormTypeOption = BaseFormType & {
  type: FormType.RadioGroup;
  options: Option[];
  initialValue?: any;
};

export type RulesType = BaseFormType & {
  type: FormType.Rules;
  options: ExOption[];
  initialValue?: any;
};

export type AreaFieldsPanelProps = BaseFormType & {
  type: FormType.TdsArea;
  className?: string
  style?: CSSProperties;
  fieldScope: Zone;
  hidden?: boolean,
};

export type FormEntry = RulesType | TimeRangeFormType | TimeRangeCascadeFormType | ProDatePickerFormType
| InputFormType
| SelectFormTypeField | SelectFormTypeOption
| SelectGroupFormTypeField | SelectGroupFormTypeOption
| SelectInputFormType | SelectNumberFormType
| SwitchFormType | RadioGroupFormTypeOption
| VersionSelectFormType
| CustomFieldsFormType | CustomKeysFormType | BuglyCustomKeysFormType | AutoCompleteFormType
| VersionSelectGroupFormType | DetailSelectGroupFormType | DateTimeRangeFormType
| CharacterInputFormType | AreaFieldsPanelProps;

interface IProps {
  entries: FormEntry[];
  onFinish: (value: Record<string, any>, compareSwitch?: boolean) => void;
  formChange?: (value: Record<string, any>, fields: Record<string, any>) => void;
  onChange?: (value: Record<string, any>, fields: Record<string, any>) => void;
  onReset?: () => void;
  form: FormInstance;
  labelWidth?: number;
  defaultCollapsed?: boolean;
  span?: number;
  // 预留字段，后续可以根据entry做一些差异化处理
  pageEntry?: FilterEntry;
  // 缓存key的前缀，当搜索条件有变更，可以通过更新前缀初始化缓存
  cachePrefix?: string;
  issueType?: number;
  onDeletedKeys?: (keys: string[]) => void;
  defaultValues?: any;
  // 当前页面是否有对比模式
  hasCompare?: boolean;
  // 是否隐藏过滤面板
  hiddenFilterPanel?: boolean;
  className?: string;
  sliceItemNumber?: number;
  // 是否隐藏查询按钮
  hiddenSubmit?: boolean;
  // 外部控制对比模式
  compareVisible?: boolean;
  hiddenCompareButton?: boolean;

  hiddenCompareState?: boolean;
  formProps?: QueryFilterProps;
  isSaveSearchValues?: boolean;
  searchValues?: SearchValueStorageType;
  setSearchValues?: (valueList: SearchValueStorageType) => void;
  submitButtonText?: string; // 提交按钮文字
  renderByGroup?: boolean;
}
export interface IFormRef {
  showFormItem: (formItem: FormEntry[], formValues: Record<string, any>) => Promise<string[]>;
}

export const QueryForm = forwardRef((
  props: IProps,
  ref: React.ForwardedRef<IFormRef>,
) => {
  const {
    entries,
    onFinish,
    onChange,
    onReset,
    form,
    labelWidth,
    defaultCollapsed,
    pageEntry,
    cachePrefix = 'prefix',
    issueType,
    hasCompare,
    hiddenFilterPanel,
    compareVisible,
    hiddenSubmit = false,
    hiddenCompareButton = false,
    formProps,
    isSaveSearchValues,
    searchValues,
    setSearchValues,
    submitButtonText = '查询',
  } = props;

  const { isFreeProduct } = useStores().ProductStore;
  const isFree = isFreeProduct();
  const [isCompareOpen, setIsCompareOpen] = useState(props?.defaultValues?.isCompareOpen);
  const [searchCount, setSearchCount] = useState(0);
  const enableEntries = isCompareOpen && hasCompare
    ? entries.filter(entry => !entry.compareDisabled)
    : entries.filter(entry => !entry.disabled);

  // 默认选项，不可编辑
  const defaultEntries = enableEntries.filter(entry => entry.deleteable === false).map(entry => entry.name);
  const [itemNumber, setItemNumber] = useState(0);
  // 用于存储在【添加字段里】取消勾选了，但是有value值的表单项
  const [filterValuesNames, setFilterValuesNames] = useState<string[]>([]);


  // 可编辑
  const editableEntries = enableEntries.filter(entry => entry.deleteable !== false);
  const cacheKey = `${cachePrefix}-${pageEntry}-query-filter-v2`;

  useEffect(() => {
    if (compareVisible === false) {
      exitCompare();
    } else if (compareVisible === true) {
      enterCompare();
    }
  }, [compareVisible]);


  // 筛选
  const filterValueItems = useMemo(
    () => {
      const cachedFilters = getCachedFilters(cacheKey);
      return enableEntries.filter(item => filterValuesNames?.includes(String(item.name))
        && !cachedFilters?.includes(String(item.name)));
    },
    [filterValuesNames, enableEntries],
  );

  const getInitEntries = () => {
    // 没传不缓存
    if (!pageEntry) return editableEntries;
    const cachedFilters = getCachedFilters(cacheKey);
    if (cachedFilters) {
      // 这里要做排序
      return cachedFilters.map((column: string) => editableEntries.find(entry => entry.name === column))
        .filter((entry: any) => !!entry) as FormEntry[];
      // return editableEntries.filter(entry => cachedFilters.includes(entry.name));
    }

    // 默认只显示不可删除的
    return [];
  };

  const [selectedEntries, setSelectedEntries] = useState(getInitEntries());
  const [collapsedStorage, setCollapsedStorage] = useLocalStorageState<Record<string, boolean>>(
    'form-collapsed-storage',
    { defaultValue: {} },
  );
  useImperativeHandle(ref, () => ({
    showFormItem,
  }));

  const showFormItem = async (formItem: FormEntry[], formValues: Record<string, any>) => {
    const cachedFilters = getCachedFilters(cacheKey);
    const list: string[] = [...new Set((filterValuesNames || []).concat(filterFormValues(formItem
      ?.filter(name => name.deleteable !== false
        && !cachedFilters?.includes(String(name?.name))
        && !selectedEntries?.some(item => item.name === name.name)), formValues)))];
    setFilterValuesNames(list);
    return list;
  };

  /**
   * 当选择的筛选项少于1行时，默认收起
   * @param entries
   */
  const onSelectedChange = (entries: FormEntry[]) => {
    setSelectedEntries(entries);
    setCachedFilters(cacheKey, entries.map(entry => entry.name));
    const needCollapsed = (entries.length
      + defaultEntries.length
      + (!hiddenFilterPanel && !isCompareOpen ? 1 : 0)) < itemNumber;
    handleCollapse(needCollapsed);
    // 删除的字段
    const deletedKeys = difference(selectedEntries.map(entry => entry.name), entries.map(entry => entry.name));
    form.resetFields(deletedKeys);
    deletedKeys.length > 0 && props.onDeletedKeys?.(deletedKeys);
    handleCollapse(needCollapsed);
  };

  const handleCollapse = (collapsed: boolean) => {
    setCollapsedStorage(state => ({ ...state, [pageEntry as string]: collapsed }));
  };

  const divRef = useRef<HTMLDivElement | null>(null);

  const getItemNumber = () => {
    const itemWidth = 290;
    const compareBtnWidth = hasCompare ? 66 : 0;
    const compareLabelWidth = isCompareOpen ? 55 : 0;
    const sliceItemNumber = 2;
    const clientWidth = divRef.current?.clientWidth as number - (compareBtnWidth + compareLabelWidth);
    if (props.sliceItemNumber === DEFAULT_SLICE_ITEM_NUMBER) {
      return Math.floor(clientWidth / itemWidth);
    }
    return Math.floor((clientWidth - (sliceItemNumber * itemWidth)) / itemWidth);
  };

  const getSpan = () => {
    if (!divRef.current) return 8;

    const itemNumber = getItemNumber();
    return 24 / itemNumber;
  };


  const [span, setSpan] = useState(getSpan());

  const switchForm = (entry: FormEntry) => {
    console.log('switchForm', entry);
    switch (entry.type) {
      case FormType.TimeRange:
        return <TimeRangeForm entry={entry} form={form as FormInstance} />;
      case FormType.TimeRangeCascade:
        return <TimeRangeCascadeForm entry={entry} form={form as FormInstance} defaultValues={props?.defaultValues} />;
      case FormType.ProDatePicker:
        return <ProFormField {...entry}>
          <ProBuglyDatePicker />
        </ProFormField>;
      case FormType.CharacterInput:
        return <CharacterInputForm entry={entry} form={form} />;
      case FormType.Input:
        return (
          <InputForm
            entry={entry}
            searchCount={searchCount}
            form={form as FormInstance}
            onChange={props.formChange}
          />
        );
      case FormType.Select:
        return <SelectForm entry={entry} form={form as FormInstance} onChange={props.formChange} />;
      case FormType.SelectGroup:
        return <SelectGroupForm entry={entry} />;
      case FormType.SelectInputGroup:
        return <SelectInputForm entry={entry} />;
      case FormType.SelectNumberGroup:
        return <SelectNumberForm entry={entry} form={form as FormInstance} formChange={props.formChange} />;
      case FormType.Switch:
        return <SwitchForm entry={entry} />;
      case FormType.RadioGroup:
        return <RadioGroupForm entry={entry} />;
      case FormType.VersionSelect:
        return <VersionSelectForm entry={entry} />;
      case FormType.VersionSelectGroup:
        return <VersionSelectGroupForm entry={entry} form={form as FormInstance} />;
      case FormType.DetailSelectGroup:
        return <DetailSelectGroupForm entry={entry} form={form as FormInstance} />;
      case FormType.CustomFields:
        return <CustomFields type={'customFields'} entry={entry} issueType={issueType} />;
      case FormType.CustomKeys:
        return <CustomFields type={'customKey'} entry={entry} issueType={issueType} />;
      case FormType.BuglyCustomKeys:
        return <CustomFields type={'buglyCustomKey'} entry={entry} issueType={issueType} />;
      case FormType.AutoComplete:
        return <AutoCompleteForm entry={entry} />;
      case FormType.DateTimeRange:
        return <DateTimeRange entry={entry} />;
      case FormType.Rules:
        return <RulesFields entry={entry}/>;
      case FormType.TdsArea:
        return <AreaForm {...entry}/>;
      default:
        return null;
    }
  };

  const renderEntries = () => {
    const finalEntries = enableEntries
      .filter((entry) => {
        const entryKeys = selectedEntries.map(item => item.name);
        return entryKeys.includes(entry.name) || defaultEntries.includes(entry.name);
      }).concat(filterValueItems);
      // 按选择顺序做排序
    const defaultSortItems = finalEntries.filter(item => selectedEntries
      ?.findIndex(entry => entry.name === item.name) === -1);
    const sortItems = selectedEntries?.
      map(item => finalEntries.find(subItem => subItem.name === item.name) as FormEntry) ?? [];
    const finalItems = defaultSortItems.concat(sortItems).filter(item => !!item);

    return finalItems
      .filter(item => !!item)
      .filter((item) => {
      // 是免费产品时过滤非免费表单
        if (isFree) {
          return item?.formType?.includes?.(ProductType.FREE_PRODUCT);
        }
        return true;
      })
      .map((entry: FormEntry) => switchForm(entry));
  };

  const isFiltersName = (name: FieldKey) => [
    FieldKey.AppVersion,
    FieldKey.OsVersion,
    FieldKey.IsInferenceFeature,
  ].includes(name);

  const newFileters = [
    FieldKey.StandardAbTestTag,
    FieldKey.StandardShiplyTag,
    FieldKey.StandardAppVersion,
    FieldKey.StandardSystemStack,
    FieldKey.StandardMonitoredThreadName,
    FieldKey.StandardBundleId,
    FieldKey.StandardFuncName,
    FieldKey.StandardFeature,
    FieldKey.StandardClientIdentify,
    FieldKey.StandardProcessLaunchId,
    FieldKey.StandardLaunchId,
    FieldKey.StandardQimei,
    FieldKey.StandardAccountId,
    FieldKey.StandardPlatform,
    FieldKey.StandardModel,
    FieldKey.StandardOsVersion,
    FieldKey.StandardProcessName,
    FieldKey.StandardScene,
    FieldKey.StandardSdkVersion,
    FieldKey.StandardBrand,
    FieldKey.StandardGround,
    FieldKey.StandardVersionMode,
    FieldKey.StandardRom,
  ];

  const renderCompareEntries = () => {
    const newEnableEntries = enableEntries
      .filter((entry) => {
        const entryKeys = selectedEntries.map(item => item.name);
        return entryKeys.includes(entry.name) || defaultEntries.includes(entry.name);
      }).concat(filterValueItems)
      .map((entry: FormEntry) => {
        const fieldName = newFileters.includes(entry.name as FieldKey) ? toHumpString(entry.name) : entry.name;

        return {
          ...entry,
          isCompareOpen,
          name: isFiltersName(entry.name as FieldKey) ? `${fieldName}_compare` : `${fieldName}Compare`,
        };
      })
      .filter((item) => {
        // 是免费产品时过滤非免费表单
        if (isFree) {
          return item?.formType?.includes?.(ProductType.FREE_PRODUCT);
        }
        return true;
      })
      .map((entry: FormEntry) => switchForm(entry));
    return newEnableEntries;
  };

  const firstLoad = useRef(true);

  useEffect(() => {
    setSpan(getSpan());
    setItemNumber(getItemNumber());
    setSelectedEntries(getInitEntries());
    if (isCompareOpen) {
      // 首次加载不执行
      if (firstLoad.current) {
        firstLoad.current = false;
        return;
      }
      const formValues = form.getFieldsValue();
      onFinish({ ...formValues, isCompareOpen }, true);
    }
  }, [isCompareOpen]);

  useEffect(() => {
    setSelectedEntries(getInitEntries());
    if (props.defaultValues?.isCompareOpen) {
      setIsCompareOpen(props.defaultValues.isCompareOpen);
    }
  }, [props.defaultValues]);

  // 取缓存值，默认展开
  const collapsed = () => {
    if (filterValuesNames.length > itemNumber) {
      return false;
    }
    return collapsedStorage?.[pageEntry as string]
      || (defaultEntries.length + selectedEntries.length
        + filterValueItems.length
        + (!hiddenFilterPanel && !isCompareOpen ? 1 : 0)) < itemNumber;
  };

  const exitCompare = () => {
    setIsCompareOpen(false);
    const formValues = form.getFieldsValue();
    const values: Record<string, any> = {};
    Object.keys(formValues).filter(item => !(item.includes('Compare') || item.includes('compare')))
      .forEach((item) => {
        values[item] = formValues[item];
      });
    onFinish({ ...values, isCompareOpen: false });
  };

  const enterCompare = () => {
    if (firstLoad.current) {
      firstLoad.current = false;
    }
    const fields = form.getFieldsValue();
    const fieldsCompare: Record<string, any> = {};
    Object.keys(fields).forEach((key) => {
      if (key !== 'filter') {
        fieldsCompare[`${key}Compare`] = fields[key];
      }
    });
    form.setFieldsValue(fieldsCompare);
    setIsCompareOpen(true);
    const pgId = getPgIdByIssueType(issueType!, pageEntry, true);
    reportClick(pgId, CUSTOM_ACTIONS.commonEnterContrastMode, {
      from: pgId,
    });
  };

  useEffect(() => {
    if (isCompareOpen && hasCompare && (renderEntries().length > itemNumber)) {
      setSpan(24);
      return;
    }
    setSpan(getSpan());
    setItemNumber(getItemNumber());
  }, [isCompareOpen, renderEntries().length]);

  const renderCompareBtn = () => (
    <Fragment>
      {isCompareOpen
        ? <Button onClick={exitCompare} style={{ marginLeft: '18px' }}>退出对比</Button>
        : <FreeProductGuide
          guideType={GuideType.POP_CONFIRM}
          display={Display.BLOCK}
          reportGuideType={ReportGuideType.Bubble}
          reportScene={ReportScene.EnterCompare}
        ><Button onClick={enterCompare} style={{
          marginLeft: '18px',
          border: '1px solid #0A6CFF',
          color: '#0A6CFF',
        }}>条件对比</Button>
        </FreeProductGuide>}
    </Fragment>
  );


  useEffect(() => {
    // 考虑到有些字段通过开关控制，需要重新更新
    setSelectedEntries(getInitEntries());
  }, [entries]);

  const isFilterPanel = !hiddenFilterPanel && !isCompareOpen;
  return (
    <div ref={divRef}>
      <QueryFilter
        preserve
        className={classNames(
          props.className,
          stylesForm.colFitContent,
          styles.queryFilter,
          collapsed() ? styles.collapsed : '',
          (isCompareOpen && hasCompare) ? styles.compare : '',
          isSaveSearchValues && !isCompareOpen ? styles.saveSearch : '',
          isFilterPanel ? styles.filterPanel : '',
        )}
        layout="vertical"
        onCollapse={handleCollapse}
        collapsed={collapsed()}
        form={form}
        labelWidth={labelWidth ?? 112}
        defaultCollapsed={defaultCollapsed}
        span={props.span ?? span}
        onFinish={async (values) => {
          (hasCompare && isCompareOpen)
            ? onFinish({ ...values, isCompareOpen })
            : onFinish(values);
          setSearchCount(count => count + 1);
        }}
        onValuesChange={onChange}
        onReset={onReset}
        submitter={{
          searchConfig: {
            submitText: submitButtonText,
          },
          resetButtonProps: { hidden: true },
          submitButtonProps: { hidden: hiddenSubmit },
          render: (_props, dom) => ([
            <div className={styles.compareRadios}>
              {isSaveSearchValues
                ? <SaveSearch
                  filterForm={form}
                  isCompareOpen={isCompareOpen}
                  onFinish={onFinish}
                  searchValues={searchValues} setSearchValues={setSearchValues} /> : null}
                <div style={{ margin: 'auto' }}>
                  {hasCompare && !hiddenCompareButton ? renderCompareBtn() : null}
                </div>
              </div>,
            dom,
          ]),
        }}
        {...formProps}
      >
        {isCompareOpen && hasCompare
          ? <FormCompare
            collapsed={collapsed()}
            renderEntries={renderEntries}
            renderCompareEntries={renderCompareEntries}
            defaultCompareNumber={itemNumber}
          />
          : renderEntries()}
        {!hiddenFilterPanel ? <div className={styles.formAfterAdd}>
          <FilterPanel
            entries={editableEntries}
            defaultSelectedEntries={selectedEntries}
            onSelectedChange={onSelectedChange}
            renderByGroup={props.renderByGroup}
          />
        </div> : null}
      </QueryFilter>
    </div>
  );
});
